import React, { useState } from "react";
import {
  Shield,
  Layers,
  RefreshCw,
  TestTube,
  Thermometer,
  CheckCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
//@ts-ignore
import caida from "../../img/caida.mp4";

export const DeviceResistance = () => {
  const [isPlaying, setIsPlaying] = useState(true);
  const { t } = useTranslation();

  const features = [
    {
      icon: <Layers className="w-6 h-6" />,
      title: t("product.features.multi_layer_absorption.title"),
      description: t("product.features.multi_layer_absorption.description"),
      metric: t("product.features.multi_layer_absorption.metric"),
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: t("product.features.reinforced_core_structure.title"),
      description: t("product.features.reinforced_core_structure.description"),
      metric: t("product.features.reinforced_core_structure.metric"),
    },

    {
      icon: <TestTube className="w-6 h-6" />,
      title: t("product.features.tested_durability.title"),
      description: t("product.features.tested_durability.description"),
      metric: t("product.features.tested_durability.metric"),
    },
    {
      icon: <Thermometer className="w-6 h-6" />,
      title: t("product.features.temperature_resistant.title"),
      description: t("product.features.temperature_resistant.description"),
      metric: t("product.features.temperature_resistant.metric"),
    },
  ];

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800 py-20 lg:py-28">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute top-0 left-0 w-full h-full"
          style={{
            backgroundImage: `radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
                                radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%),
                                radial-gradient(circle at 40% 40%, rgba(120, 200, 255, 0.3) 0%, transparent 50%)`,
          }}
        ></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Video Side */}
          <div className="relative order-2 lg:order-1">
            {/* Video container with enhanced styling */}
            <div className="relative group">
              {/* Glow effect */}
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500"></div>

              {/* Video wrapper */}
              <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-2 shadow-2xl">
                <div className="relative rounded-2xl overflow-hidden bg-black">
                  {/* Video real en vez de animación */}
                  <div className="aspect-video flex items-center justify-center relative">
                    <video
                      src={caida}
                      className="w-full h-full object-cover rounded-2xl"
                      autoPlay={isPlaying}
                      loop
                      muted
                      playsInline
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                      id="caida-video"
                    />
                  </div>

                  {/* Video controls overlay */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="bg-black/60 backdrop-blur-sm rounded-lg px-4 py-2 flex items-center justify-between">
                      <span className="text-white text-sm font-medium">
                        {t("product.impact_test_simulation")}
                      </span>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                        <span className="text-white text-xs">
                          {t("home.wizard_device.main_image.live")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>


            </div>
          </div>

          {/* Content Side */}
          <div className="space-y-8 order-1 lg:order-2">
            {/* Header */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-3 px-4 py-2 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full border border-blue-500/30 text-blue-300 font-medium text-sm">
                <Shield className="w-4 h-4" />
                {t("product.advanced_protection")}
              </div>

              <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight">
                {t("product.impact_resistance")}
              </h2>

              <p className="text-xl text-gray-300 leading-relaxed">
                {t("product.resistance_description")}
              </p>
            </div>

            {/* Features */}
            <div className="space-y-4">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="group relative bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-6 hover:border-blue-500/50 transition-all duration-300 hover:bg-gradient-to-br hover:from-gray-700/50 hover:to-gray-800/50"
                >
                  <div className="flex items-start gap-4">
                    <div className="hidden lg:flex flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl  items-center justify-center text-white group-hover:scale-110 transition-transform duration-300">
                      {feature.icon}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <h3 className="text-lg font-semibold text-white transition-colors duration-300 group-hover:text-blue-300">
                          {feature.title}
                        </h3>
                        <span className="inline-flex w-fit rounded-full bg-blue-500/20 px-3 py-1 text-sm font-medium text-blue-400 sm:ml-4 sm:shrink-0">
                          {feature.metric}
                        </span>
                      </div>
                      <p className="text-gray-400 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>

                  {/* Hover glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                </div>
              ))}
            </div>

            {/* Summary */}
          </div>
        </div>
      </div>
    </section>
  );
};
